import "./ListingContent.scss";
import { useState, useEffect } from "react";
import classNames from "classnames";
import { LISTING_ITEM_TYPES, RESOLUTION } from "app/constants";
import ListingItemCard from "app/pages/Listing/ListingContent/ListingItemCard";
import PropTypes from "prop-types";
import { merchShape } from "app/utils/propTypes";
import { useIntersectionObserver } from "app/hooks";
import { useFacets, useFilterContext } from "app/features/product/productFilters";

const PRODUCTS_PER_CHUNK = 12;

const rowOnGrid = (allMerchs, currentMerch, isMobile) => {
	const merchsBeforeCurrentMerch = allMerchs.filter(
		merch => merch?.position !== 0 && merch?.position < currentMerch?.position
	);

	const rowPositionWithoutMerchBefore = isMobile
		? currentMerch?.position
		: Math.floor((currentMerch?.position - 1) / 2) + 1;

	return merchsBeforeCurrentMerch?.length > 0
		? rowPositionWithoutMerchBefore + merchsBeforeCurrentMerch?.length
		: rowPositionWithoutMerchBefore;
};

const insertMerchAtCorrectPosition = (accommodations, merchs, shouldInsertMerch = true) => {
	const result = [];
	let productIndex = 0;

	if (!shouldInsertMerch) {
		return accommodations;
	}

	accommodations.forEach(product => {
		productIndex++;

		const merchsForThisPosition = merchs.filter(merch => merch?.position === productIndex);
		result.push(...merchsForThisPosition, product);
	});

	const remainingMerchs = merchs.filter(merch => merch?.position > accommodations.length);
	result.push(...remainingMerchs);

	return result;
};

const ListingContent = ({ resolution, merchsListingItems, activeProductsFilters = [] }) => {
	const { filteredProducts, activeFilters } = useFacets();
	const { selectedFiltersCount } = useFilterContext();

	const isAnyListingFilterActive = selectedFiltersCount > 0;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const [loadMoreRef, loadMoreRefInView] = useIntersectionObserver({
		rootMargin: "0px 0px 600px 0px",
	});

	const [visibleAccommodationsCount, setVisibleAccommodationsCount] = useState(
		PRODUCTS_PER_CHUNK
	);

	const visibleAccommodations = filteredProducts.slice(0, visibleAccommodationsCount);
	const visibleProducts = insertMerchAtCorrectPosition(
		visibleAccommodations,
		merchsListingItems,
		!isAnyListingFilterActive
	);

	const hasMoreToShow = filteredProducts.length > visibleAccommodationsCount;

	useEffect(() => {
		if (!loadMoreRefInView) {
			return;
		}

		if (hasMoreToShow) {
			setVisibleAccommodationsCount(prev => prev + PRODUCTS_PER_CHUNK);
		}
	}, [loadMoreRefInView, hasMoreToShow]);

	useEffect(() => {
		setVisibleAccommodationsCount(PRODUCTS_PER_CHUNK);
	}, [activeFilters]);

	return (
		<div className="listing-content">
			<div className="listing-content-grid">
				{visibleProducts.map((listingItem, index) => {
					const itemClass = classNames({
						"listing-content-grid__item": ![
							LISTING_ITEM_TYPES.MERCH_SIMPLE,
							LISTING_ITEM_TYPES.MERCH_CAROUSSEL,
						].includes(listingItem.type),
						"listing-content-grid__item--card": true,
						[`listing-content-grid__item--merchandising-simple-row-${rowOnGrid(
							merchsListingItems,
							listingItem,
							isMobile
						)}`]: listingItem.type === LISTING_ITEM_TYPES.MERCH_SIMPLE,
						[`listing-content-grid__item--merchandising-caroussel-row-${rowOnGrid(
							merchsListingItems,
							listingItem,
							isMobile
						)}`]: listingItem.type === LISTING_ITEM_TYPES.MERCH_CAROUSSEL,
					});

					const key =
						(listingItem.id && `${listingItem.id}${listingItem.startAt}`) ||
						(listingItem.merchandising &&
							(listingItem.merchandising.code || listingItem.merchandising[0].code));

					const shouldShow =
						!isAnyListingFilterActive ||
						(isAnyListingFilterActive &&
							listingItem.type === LISTING_ITEM_TYPES.PRODUCT);

					return (
						shouldShow && (
							<div key={key} className={itemClass}>
								<ListingItemCard
									resolution={resolution}
									hideMerchandising={isAnyListingFilterActive}
									product={listingItem}
									activeProductsFilters={activeProductsFilters}
									position={index}
								/>
							</div>
						)
					);
				})}
			</div>

			<div ref={loadMoreRef} aria-hidden="true" />
		</div>
	);
};

ListingContent.propTypes = {
	resolution: PropTypes.string,
	merchsListingItems: PropTypes.arrayOf(merchShape),
	activeProductsFilters: PropTypes.array,
};

export default ListingContent;
